// `report.pins` is never reported: seqOrder is as-built, so every order defect would produce it.
import type { InferenceReport } from "./cdtInfer";

const RESOLVER_SIZE_ATTR = "__sizeLabel";
const STUDIO_SIZE_ATTR = "size";

const studioAttr = (attr: string, quantityAttribute: string): string =>
  attr === RESOLVER_SIZE_ATTR ? quantityAttribute : attr;

export type InferenceExceptionKind =
  | "orient-disagrees"
  | "no-order"
  | "contrary-block"
  | "order-drift"
  | "gradient-section"
  | "size-unparsed"
  | "unit-mix"
  | "pin-backwards";

export const ALERT_KINDS: ReadonlySet<InferenceExceptionKind> = new Set<InferenceExceptionKind>([
  "size-unparsed",
  "unit-mix",
  "gradient-section",
]);

export interface InferenceException {
  id: string;
  kind: InferenceExceptionKind;
  title: string;
  detail: string;
  attr: string | null;
  scopeKey: string | null;
  skuIds: readonly string[];
}

export interface LevelConfidence {
  attr: string;
  label: string;
  best: number | null;
  vorder: number | null;
  pins: number | null;
  magnitude: number | null;
  convention: boolean;
}

export interface InferenceSummary {
  confidenceByAttr: Readonly<Record<string, LevelConfidence>>;
  exceptions: readonly InferenceException[];
}

const pct = (x: number | null): string => (x == null ? "—" : `${Math.round(100 * x)}%`);

export type ContraryBlockJudge = (
  depth: number,
  blockKey: string,
  declared: "horizontal" | "vertical",
) => "keep" | "drop";

/** Pair tally cannot tell a wrapped run from true shelf bands. */
export type LevelOrientationJudge = (
  depth: number,
  attr: string,
  declared: "horizontal" | "vertical",
) => "keep" | "drop";

export interface InferenceJudges {
  contrary?: ContraryBlockJudge;
  level?: LevelOrientationJudge;
  /** Must match `StudioTreeCanvas`. */
  bandedDepth?: number;
  skuIdsForAttrValues?: (attr: string, values: readonly string[]) => readonly string[];
  quantityAttribute?: string;
}

export const summarizeInference = (
  report: InferenceReport,
  judges: InferenceJudges | ContraryBlockJudge = {},
): InferenceSummary => {
  const {
    contrary: contraryJudge,
    level: levelJudge,
    bandedDepth = -1,
    quantityAttribute = STUDIO_SIZE_ATTR,
    skuIdsForAttrValues,
  } = typeof judges === "function"
    ? {
        contrary: judges,
        level: undefined,
        bandedDepth: -1,
        quantityAttribute: STUDIO_SIZE_ATTR,
        skuIdsForAttrValues: undefined,
      }
    : judges;
  const toStudioAttr = (attr: string) => studioAttr(attr, quantityAttribute);
  const confidenceByAttr: Record<string, LevelConfidence> = {};
  for (const candidate of report.candidates) {
    const best = Math.max(candidate.vorder ?? 0, candidate.pins ?? 0, candidate.magnitude ?? 0);
    const measurable =
      candidate.vorder != null || candidate.pins != null || candidate.magnitude != null;
    confidenceByAttr[toStudioAttr(candidate.attr)] = {
      attr: toStudioAttr(candidate.attr),
      label: candidate.label,
      best: measurable ? best : null,
      vorder: candidate.vorder,
      pins: candidate.pins,
      magnitude: candidate.magnitude,
      convention: measurable && best < 0.6,
    };
  }
  const quantityLabel = confidenceByAttr[quantityAttribute]?.label ?? "Quantity";

  // Engine "horizontal" is vertical blocking only above the first banded level.
  const merch = (depth: number, orientation: string): string => {
    if (orientation === "vertical") return "separate shelf rows (each value on its own shelves)";
    return bandedDepth >= 0 && depth < bandedDepth
      ? "vertical columns (each value a full-height column)"
      : "left-to-right groups (each value a stretch along the shelf)";
  };
  const merchShort = (depth: number, orientation: string): string => {
    if (orientation === "vertical") return "separate shelf rows";
    return bandedDepth >= 0 && depth < bandedDepth ? "vertical columns" : "left-to-right groups";
  };
  const opposite = (orientation: string): string =>
    orientation === "horizontal" ? "vertical" : "horizontal";

  const exceptions: InferenceException[] = [];

  for (const level of report.levels.filter((entry) => !entry.agrees)) {
    if (levelJudge && levelJudge(level.depth, level.attr, level.declared) === "drop") continue;
    exceptions.push({
      id: `orient:${level.attr}`,
      kind: "orient-disagrees",
      title: `${level.label} grouping differs from the proposed arrangement`,
      detail:
        `Proposed: ${merch(level.depth, level.declared)}. Observed across ${level.pairs} ` +
        `comparable pair(s) of ${level.label} values: ${pct(level.xOnly / (level.pairs || 1))} ` +
        `separated along the shelf, ${pct(level.yOnly / (level.pairs || 1))} on separate ` +
        "shelf rows. Review which arrangement future layouts should follow.",
      attr: toStudioAttr(level.attr),
      scopeKey: null,
      skuIds: [],
    });
  }

  for (const candidate of Object.values(confidenceByAttr).filter((entry) => entry.convention)) {
    exceptions.push({
      id: `no-order:${candidate.attr}`,
      kind: "no-order",
      title: `Confirm the preferred ${candidate.label} order`,
      detail:
        `The existing layout does not clearly support one of the tested ${candidate.label} ` +
        `orders. Best tested order matched ${pct(candidate.best)} of comparable pairs. ` +
        "Choose the order future layouts should follow.",
      attr: candidate.attr,
      scopeKey: null,
      skuIds: [],
    });
  }

  for (const level of report.levels) {
    for (const block of level.contrary) {
      if (contraryJudge && contraryJudge(level.depth, block.key, level.declared) === "drop") {
        continue;
      }
      exceptions.push({
        id: `contrary:${level.attr}:${block.key}`,
        kind: "contrary-block",
        title: `${block.key || "This plan"} uses a different arrangement`,
        detail:
          `Within this group, ${level.label} values ${
            level.declared === "horizontal"
              ? `sit on separate shelf rows in ${block.y} pair(s), and side by side in ${block.x}`
              : `stand side by side as vertical columns in ${block.x} pair(s), and on separate shelf rows in ${block.y}`
          }. The proposed general arrangement uses ${merchShort(level.depth, level.declared)}. ` +
          "Review whether this group should keep its own arrangement.",
        attr: toStudioAttr(level.attr),
        scopeKey: block.key || null,
        skuIds: [],
      });
    }
  }

  for (const order of report.order.filter(
    (entry) => entry.disc > 0 && entry.agreement != null && entry.agreement < 0.9,
  )) {
    const pairValues = new Set<string>();
    const sentences = order.worst.map(({ pair, n }) => {
      const [wantedFirst, wantedSecond] = pair.split("  ·before·  ");
      pairValues.add(wantedFirst);
      pairValues.add(wantedSecond);
      return `suggested ${wantedFirst} → ${wantedSecond}, but ${n} reversed comparison(s) show ${wantedSecond} → ${wantedFirst}`;
    });
    const comparisons = order.conc + order.disc;
    exceptions.push({
      id: `order-drift:${order.attr}`,
      kind: "order-drift",
      title: `Some products differ from the suggested ${order.label} order`,
      detail:
        sentences.join("; ") +
        `. ${order.disc} of ${comparisons} comparable pair(s) run against the suggested order.`,
      attr: toStudioAttr(order.attr),
      scopeKey: null,
      skuIds: skuIdsForAttrValues ? [...skuIdsForAttrValues(order.attr, [...pairValues])] : [],
    });
  }

  for (const section of report.gradient.sections.filter((entry) => entry.bad > 0)) {
    exceptions.push({
      id: `gradient:${section.sec}`,
      kind: "gradient-section",
      title: `${section.sec}: pack heights do not consistently increase toward lower shelves`,
      detail:
        "Median pack height by shelf, top to bottom: " +
        section.rows.map((row) => `shelf ${row.shelf} ${row.h.toFixed(0)}cm`).join(", ") +
        `. ${section.bad} of ${section.ok + section.bad} step(s) run the other way. ` +
        "Should taller packs be placed on lower shelves here?",
      attr: null,
      scopeKey: section.sec,
      skuIds: [],
    });
  }

  if (report.sizes.unparsed.length > 0) {
    // One alert, not one row per SKU. Counted as products: one product can carry several rows.
    const unparsedProducts = [
      ...new Set(report.sizes.unparsed.map((sku) => sku.id.split("::")[0])),
    ];
    exceptions.push({
      id: "size-unparsed",
      kind: "size-unparsed",
      title: `Pack-size information needs checking for ${unparsedProducts.length} product(s)`,
      detail:
        `We could not read the ${quantityLabel.toLowerCase()} for ` +
        report.sizes.unparsed
          .map((sku) => `${sku.id.split("::")[0]} ("${sku.label}", ${sku.w}×${sku.h})`)
          .join(", ") +
        ". Size ordering may use an estimate based on pack dimensions. Check their " +
        "pack-size labels before relying on the order.",
      attr: quantityAttribute,
      scopeKey: null,
      skuIds: unparsedProducts,
    });
  }

  for (const block of report.sizes.mixedBlocks) {
    exceptions.push({
      id: `unit-mix:${block.node}`,
      kind: "unit-mix",
      title: `${block.node}: these pack sizes use different measures`,
      detail:
        "This group contains " +
        Object.entries(block.families)
          .map(([family, count]) => `${count} ${family}`)
          .join(", ") +
        ". These measures cannot all be compared directly, so a default ordering is used: " +
        "count, then loads, then mass, then volume. Review whether that matches your intent.",
      attr: quantityAttribute,
      scopeKey: block.node,
      skuIds: [],
    });
  }

  return { confidenceByAttr, exceptions };
};
